import React, {useEffect, useRef, useState} from 'react'
import {Dialog, DialogActions, DialogContent, Button} from "@mui/material";
import {observer} from "mobx-react";
import CloseIcon from '@mui/icons-material/Close';
import './modal.scss';
import {useStores} from "../../Hooks";
import Utility from "../../Utility";
import 'ckeditor5/ckeditor5.css';
import {CKEditor} from '@ckeditor/ckeditor5-react';
import './CkeEditor.css'
import {
    ClassicEditor,
    AccessibilityHelp,
    Alignment,
    Autoformat,
    AutoImage,
    AutoLink,
    Autosave,
    BlockQuote,
    Bold,
    CloudServices,
    List,
    Code,
    CodeBlock,
    Essentials,
    FindAndReplace,
    FontBackgroundColor,
    FontColor,
    FontFamily,
    FontSize,
    GeneralHtmlSupport,
    Heading,
    Highlight,
    HorizontalLine,
    HtmlComment,
    HtmlEmbed,
    ImageBlock,
    ImageInsertViaUrl,
    ImageToolbar,
    ImageUpload,
    Indent,
    IndentBlock,
    Italic,
    Link,
    Mention,
    Paragraph,
    PasteFromOffice,
    RemoveFormat,
    SelectAll,
    ShowBlocks,
    SourceEditing,
    SpecialCharacters,
    SpecialCharactersArrows,
    SpecialCharactersCurrency,
    SpecialCharactersEssentials,
    SpecialCharactersLatin,
    SpecialCharactersMathematical,
    SpecialCharactersText,
    Strikethrough,
    Style,
    Subscript,
    Superscript,
    Table,
    TableCaption,
    TableCellProperties,
    TableColumnResize,
    TableProperties,
    TableToolbar,
    TextTransformation,
    Title,
    Underline,
    Undo
} from 'ckeditor5';
import {stripKnownHTMLTags} from "../../serverClientShared/sharedUtils";

const RichTextCkeEditorDialog = observer(() => {
    const {modalStore, generalStore} = useStores();
    let initialValue = useRef('')
    const [headerTooLong, setHeaderTooLong] = useState(false);
    const [descriptionTooLong, setDescriptionTooLong] = useState(false);
    const [editorValue, setEditorValue] = useState('');
    const [editorInstance, setEditorInstance] = useState(null);
    const [isLayoutReady, setIsLayoutReady] = useState(false);
    const [headerLength, setHeaderLength] = useState(0);
    const [descriptionLength, setDescriptionLength] = useState(0);
    const [isEditorReady, setIsEditorReady] = useState(false);

    const editorDivRef = useRef(null);
    const editorRef = useRef(null);

    const headerLimit = 70;
    const descriptionLimit = 2000

    useEffect(() => {
        setIsLayoutReady(true);
        return () => {
            setIsLayoutReady(false)
        };
    }, []);

    useEffect(() => {
        if (modalStore.isRichTextEditorModalOpen) {
            initialValue.current = modalStore.modalData.richText || '';
            setEditorValue(initialValue.current);
        }
        return () => {
            initialValue.current = '';
        };
    }, [modalStore.isRichTextEditorModalOpen]);

    // useEffect(() => {
    //     if (editorInstance) {
    //         editorInstance.editing.view.document.on('copy', (evt, data) => {
    //         })
    //     }
    // }, [editorInstance]);

    useEffect(() => {
        if (isEditorReady && editorInstance) {
            editorInstance.editing.view.document.on('clipboardInput', (evt, data) => {
                const selection = editorInstance.editing.view.document.selection;
                const viewElement = selection.getFirstPosition().parent;

                if (viewElement && viewElement.is('element', 'h1')) {
                    const pastedHtml = data.dataTransfer.getData('text/html');
                    const pastedText = data.dataTransfer.getData('text/plain');

                    // Check if content is from Excel
                    const isExcelContent = data.dataTransfer.types.some(type =>
                        type.includes('excel') || type.includes('ms-excel')
                    );

                    if (isExcelContent) {
                        evt.stop();
                        const plainText = data.dataTransfer.getData('text/plain');
                        if (plainText) {
                            const lines = plainText.split('\n');
                            const firstNonEmptyLine = lines.find(line => line.trim().length > 0);

                            if (firstNonEmptyLine) {
                                const model = editorInstance.model;
                                const document = model.document;

                                model.change(writer => {
                                    const position = document.selection.getFirstPosition();
                                    writer.insertText(firstNonEmptyLine.trim(), position);
                                });
                            }
                        }
                        return;
                    }

                    let contentToInsert = pastedHtml || pastedText;

                    if (pastedHtml) {
                        contentToInsert = cleanHtml(contentToInsert);
                    }

                    const currentContent = editorInstance.getData();
                    const isEmptyEditor = !currentContent.trim();

                    if (isEmptyEditor) {
                        evt.stop();

                        // Handle plain text with empty lines
                        if (pastedText) {
                            const lines = pastedText.split('\n');
                            // Find first non-empty line for header
                            const firstNonEmptyLine = lines.find(line => line.trim().length > 0);
                            // Get remaining non-empty lines for description
                            const remainingLines = lines
                                .slice(lines.indexOf(firstNonEmptyLine) + 1)
                                .filter(line => line.trim().length > 0);

                            if (firstNonEmptyLine) {
                                const headerContent = `<h1>${firstNonEmptyLine}</h1>`;
                                const descriptionContent = remainingLines.length > 0
                                    ? `<p>${remainingLines.join('</p><p>')}</p>`
                                    : '';

                                editorInstance.setData(headerContent + descriptionContent);
                            }
                        } else if (pastedHtml) {
                            // For HTML content, remove empty paragraphs and handle first non-empty content
                            const cleanedHtml = contentToInsert
                                .replace(/<p>\s*<\/p>/g, '') // Remove empty paragraphs
                                .replace(/^\s+|\s+$/g, '');  // Trim whitespace
                            editorInstance.setData(cleanedHtml);
                        }
                    }
                }
            });

            editorInstance.editing.view.document.on('paste', (evt, data) => {
                const selection = editorInstance.editing.view.document.selection;
                const viewElement = selection.getFirstPosition().parent;

                if (viewElement && viewElement.is('element', 'h1')) {
                    const clipboardData = data.dataTransfer;

                    const isExcelContent = clipboardData.types.some(type =>
                        type.includes('excel') || type.includes('ms-excel')
                    );

                    if (isExcelContent) {
                        const plainText = clipboardData.getData('text/plain');
                        if (plainText) {
                            evt.stop();
                            const model = editorInstance.model;
                            const document = model.document;

                            model.change(writer => {
                                const position = document.selection.getFirstPosition();
                                const insertText = plainText.trim();
                                writer.insertText(insertText, position);
                            });
                        }
                    }
                }
            });
        }
    }, [isEditorReady, editorInstance]);


    useEffect(() => {
        if (editorInstance) {
            editorInstance.editing.view.document.on('keydown', (event, data) => {
                if (data.keyCode === 13 && data.shiftKey) {
                    data.preventDefault();
                    event.stop();
                }
            });
        }
    }, [editorInstance]);

    function isInHeader(editor) {
        const selection = editor.editing.view.document.selection;
        const position = selection.getFirstPosition();
        if (!position) return false;

        const element = position.parent;
        return element && element.is('element', 'h1');
    }

    function convertHeadersToParagraph(text) {
        const fontSizeMap = {
            h1: '32px',
            h2: '24px',
            h3: '20px',
            h4: '18px',
            h5: '16px',
            h6: '14px',
        };

        return text.replace(/<h([1-6])>(.*?)<\/h\1>/g, (match, level, content) => {
            const fontSize = fontSizeMap[`h${level}`];
            return `<p><span style="font-size: ${fontSize};">${content}</span></p>`;
        });
    }

    const focusOnHeader = (editor) => {
        const view = editor.editing.view;
        const model = editor.model;
        const document = model.document;

        view.focus();

        model.change(writer => {
            const root = document.getRoot();
            const heading = root.getChild(0);

            if (heading && heading.name === 'title') {
                writer.setSelection(writer.createPositionAt(heading, 'end'));
            }
        });
    };


    function handleEditorReady(editor) {
        setEditorInstance(editor);
        setIsEditorReady(true);
        focusOnHeader(editor);
    }

    function cleanHtml(inputHtml) {
        const withoutHtmlBody = inputHtml.replace(/<\/?html[^>]*>/gi, '').replace(/<\/?body[^>]*>/gi, '');

        const withoutComments = withoutHtmlBody.replace(/<!--.*?-->/gs, '');

        return withoutComments.trim();
    }

    function resetTextLengthChecker() {
        setHeaderLength(0);
        setDescriptionLength(0);
        setHeaderTooLong(false);
        setDescriptionTooLong(false);
    }

    function closeModal() {
        modalStore.modalData = {};
        modalStore.isRichTextEditorModalOpen = false;
        resetTextLengthChecker();
    }


    function cancelAndCloseModal(skipConfirm) {
        if (!skipConfirm) {
            const confirmed = window.confirm(generalStore.translate('areYouSureYouWantToExitWithoutSaving'));
            if (!confirmed) {
                return;
            }
        }

        initialValue.current = '';
        closeModal();
    }


    function addBreakLine(description) {
        const newDescription = description
            .replace(/<\/(p|li|ol|ul|blockquote|h1|h2|h3|h4|h5|h6)>\n*/g, '</$1>\n')
            .replace(/<(ol|ul|blockquote)>\n*/g, '<$1>\n');
        return replaceHslWithRgb(newDescription);
    }

    function hslToRgb(h, s, l) {
        s /= 100;
        l /= 100;
        const k = n => (n + h / 30) % 12;
        const a = s * Math.min(l, 1 - l);
        const f = n => l - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1));
        return `rgb(${Math.round(f(0) * 255)}, ${Math.round(f(8) * 255)}, ${Math.round(f(4) * 255)})`;
    }

    function replaceHslWithRgb(htmlString) {
        return htmlString.replace(/hsl\((\d+),\s*(\d+)%?,\s*(\d+)%?\)/g, (match, h, s, l) => {
            return hslToRgb(h, s, l);
        });
    }

    function save(forceValue = null) {
        const newValue = forceValue || editorValue;
        let {header, description} = editorDataToHeaderAndDescription(newValue);
        description = addBreakLine(description)
        description = convertHeadersToParagraph(description);
        header = header.replace(/<h1>/g, "<p>").replace(/<\/h1>/g, "</p>")
        modalStore.modalData.onRichTextEditorSave(header, description);
        closeModal();
    }

    function editorDataToHeaderAndDescription(data) {
        const h1Match = /<h1[^>]*>(.*?)<\/h1>/s.exec(data);
        const pMatch = /<p[^>]*>(.*?)<\/p>/s.exec(data);

        let header = '';
        let description = data;

        if (h1Match && (!pMatch || h1Match.index < pMatch.index)) {
            header = h1Match[0];
            description = data.replace(header, '');
        } else if (pMatch) {
            header = pMatch[0];
            description = data.replace(header, '');
        }

        return {header, description};
    }
    const getTextLength = (html) => {
        const div = document.createElement('div');
        div.innerHTML = html;
        return div.textContent.trim();
    };
    function updateHeaderAndDescriptionLength(header, description) {
        const headerNoHtml = getTextLength(header)
        const descriptionNoHtml = getTextLength(description)
        setHeaderLength(headerNoHtml.length);
        setDescriptionLength(descriptionNoHtml.length);
        return {headerNoHtml, descriptionNoHtml};
    }

    function onEnter() {
        const currentText = modalStore.modalData.richText || '';
        setEditorValue(currentText);
        const {header, description} = editorDataToHeaderAndDescription(currentText);
        const {headerNoHtml, descriptionNoHtml} = updateHeaderAndDescriptionLength(header, description);
        if (headerNoHtml.length > headerLimit) {
            setHeaderTooLong(true);
        }
        if (descriptionNoHtml.length > descriptionLimit) {
            setDescriptionTooLong(true);
        }
        if (editorRef.current) {
            editorRef?.current?.editor?.focus();
        }
    }

    function onChange(event, editor) {
        const data = editor.getData();

        let {header, description} = editorDataToHeaderAndDescription(data);
        description = addBreakLine(description)
        const {headerNoHtml, descriptionNoHtml} = updateHeaderAndDescriptionLength(header, description);

        if (headerNoHtml.length > headerLimit) {
            setHeaderTooLong(true);
            modalStore.showErrorMessage(generalStore.translate('HeaderIsTooLong'));

            if (isInHeader(editor)) {
                return;
            }
        } else {
            if (headerTooLong) {
                modalStore.alertMessage = '';
            }
            setHeaderTooLong(false);
        }

        if (descriptionNoHtml.length > descriptionLimit) {
            setDescriptionTooLong(true);
            modalStore.showErrorMessage(generalStore.translate('DescriptionIsTooLong'));
        } else {
            if (descriptionTooLong) {
                modalStore.alertMessage = '';
            }
            setDescriptionTooLong(false);
        }

        setEditorInstance(editor);
        setEditorValue(data);
    }

    const editorConfig = {
        toolbar: {
            items: [
                'undo',
                'redo',
                '|',
                'bold',
                'italic',
                'underline',
                '|',
                'bulletedList',
                'numberedList',
                '|',
                '|',
                'link',
                '|',
                'sourceEditing',
                '|',
                'findAndReplace',
                '|',
                'superscript',
                'removeFormat',
                '|',
                'blockQuote',
            ],
            shouldNotGroupWhenFull: false
        },
        plugins: [
            AccessibilityHelp,
            Alignment,
            Autoformat,
            AutoImage,
            AutoLink,
            Autosave,
            BlockQuote,
            Bold,
            CloudServices,
            Code,
            CodeBlock,
            Essentials,
            FindAndReplace,
            FontBackgroundColor,
            FontColor,
            FontFamily,
            FontSize,
            GeneralHtmlSupport,
            Heading,
            Highlight,
            HorizontalLine,
            HtmlComment,
            HtmlEmbed,
            ImageBlock,
            ImageInsertViaUrl,
            ImageToolbar,
            ImageUpload,
            Indent,
            IndentBlock,
            Italic,
            Link,
            List,
            Mention,
            Paragraph,
            PasteFromOffice,
            RemoveFormat,
            SelectAll,
            ShowBlocks,
            SourceEditing,
            SpecialCharacters,
            SpecialCharactersArrows,
            SpecialCharactersCurrency,
            SpecialCharactersEssentials,
            SpecialCharactersLatin,
            SpecialCharactersMathematical,
            SpecialCharactersText,
            Strikethrough,
            Style,
            Subscript,
            Superscript,
            Table,
            TableCaption,
            TableCellProperties,
            TableColumnResize,
            TableProperties,
            TableToolbar,
            TextTransformation,
            Title,
            Underline,
            Undo
        ],
        fontFamily: {
            supportAllValues: true
        },
        fontSize: {
            options: [8, 10, 12, 'default', 18, 24, 36],
            supportAllValues: true
        },
        heading: {
            options: [
                {
                    model: 'paragraph',
                    title: 'Paragraph',
                    class: 'ck-heading_paragraph'
                },
                // {
                //   model: 'heading1',
                //   view: 'h1',
                //   title: 'Heading 1',
                //   class: 'ck-heading_heading1'
                // },
                {
                    model: 'heading2',
                    view: 'h2',
                    title: 'Heading 2',
                    class: 'ck-heading_heading2'
                },
                {
                    model: 'heading3',
                    view: 'h3',
                    title: 'Heading 3',
                    class: 'ck-heading_heading3'
                },
                {
                    model: 'heading4',
                    view: 'h4',
                    title: 'Heading 4',
                    class: 'ck-heading_heading4'
                },
                {
                    model: 'heading5',
                    view: 'h5',
                    title: 'Heading 5',
                    class: 'ck-heading_heading5'
                },
                {
                    model: 'heading6',
                    view: 'h6',
                    title: 'Heading 6',
                    class: 'ck-heading_heading6'
                }
            ]
        },
        htmlSupport: {
            allow: [
                {
                    name: /^.*$/,
                    styles: true,
                    attributes: true,
                    classes: true
                }
            ]
        },
        image: {
            toolbar: ['imageTextAlternative']
        },
        initialData: editorValue,
        link: {
            addTargetToExternalLinks: true,
            defaultProtocol: 'https://',
            decorators: {
                toggleDownloadable: {
                    mode: 'manual',
                    label: 'Downloadable',
                    attributes: {
                        download: 'file'
                    }
                }
            }
        },
        mention: {
            feeds: [
                {
                    marker: '@',
                    feed: [
                        /* See: https://ckeditor.com/docs/ckeditor5/latest/features/mentions.html */
                    ]
                }
            ]
        },
        placeholder: 'Write or paste your description here...',
        style: {
            definitions: [

                {
                    name: 'Article category',
                    element: 'h3',
                    classes: ['category']
                },
                {
                    name: 'Title',
                    element: 'h2',
                    classes: ['document-title']
                },
                {
                    name: 'Subtitle',
                    element: 'h3',
                    classes: ['document-subtitle']
                },
                {
                    name: 'Info box',
                    element: 'p',
                    classes: ['info-box']
                },
                {
                    name: 'Side quote',
                    element: 'blockquote',
                    classes: ['side-quote']
                },
                {
                    name: 'Marker',
                    element: 'span',
                    classes: ['marker']
                },
                {
                    name: 'Spoiler',
                    element: 'span',
                    classes: ['spoiler']
                },
                {
                    name: 'Code (dark)',
                    element: 'pre',
                    classes: ['fancy-code', 'fancy-code-dark']
                },
                {
                    name: 'Code (bright)',
                    element: 'pre',
                    classes: ['fancy-code', 'fancy-code-bright']
                }
            ]
        },
        table: {
            contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties']
        }
    };

    return (
        <Dialog
            id='rich-text-dialog'
            className='dialog big-dialog rich-text-dialog'
            open={modalStore.isRichTextEditorModalOpen}
            onClose={() => cancelAndCloseModal(initialValue.current === editorValue)}
            fullWidth
            maxWidth='md'
            TransitionProps={{
                onEnter
            }}
            keepMounted

        >
            <DialogContent>
                <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                        className='close-dialog'
                        onClick={() => cancelAndCloseModal(initialValue.current === editorValue)}>
                    <CloseIcon/>
                </Button>
                <div className='rich-editor-container' key={modalStore.isRichTextEditorModalOpen}>
                    <div className="editor-container editor-container_classic-editor editor-container_include-style"
                         ref={editorDivRef}>
                        <div className="editor-container__editor">
                            <div ref={editorRef}>
                                {isLayoutReady &&
                                    <CKEditor
                                        onChange={onChange}
                                        editor={ClassicEditor}
                                        config={editorConfig}
                                        onReady={handleEditorReady}
                                    />}
                            </div>
                        </div>
                    </div>
                </div>
            </DialogContent>
            <DialogActions>
                <div className={`header-limit-container${headerTooLong ? " header-too-long" : ""}`}>
                    {`(${headerLimit - (headerLength || 0)} ${generalStore.translate('header charactersRemaining', true)})`}
                </div>
                <div className={`description-limit-container${descriptionTooLong ? " description-too-long" : ""}`}>
                    {`(${descriptionLimit - (descriptionLength || 0)} ${generalStore.translate('description charactersRemaining', true)})`}
                </div>
                <Button variant='contained' className='cancel-button'
                        onClick={() => cancelAndCloseModal(true)}>{generalStore.translate('cancel')}</Button>
                <Button className='save-button' variant='contained'
                        onClick={() => (!headerTooLong || !descriptionTooLong) && save()}
                        disabled={!!headerTooLong || !!descriptionTooLong}>{generalStore.translate('save')}</Button>
            </DialogActions>
        </Dialog>
    )
});

export default RichTextCkeEditorDialog;
